'use client';

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { History, Trash2 } from 'lucide-react';
import SectionTitle from '@/components/ui/SectionTitle';
import { ObraCoverImage } from '@/components/obra/ObraCoverImage';
import { getObraImageUrl } from '@/utils/image-utils';
import { generateChapterLink, generateWorkLink } from '@/utils/utils';
import { clearHistory, getHistory, HISTORY_STORAGE_KEY, type HistoryEntry } from '@/utils/history';

function formatWhen(visitedAt: number): string {
    const diff = Date.now() - visitedAt;
    const min = Math.floor(diff / 60000);
    const h = Math.floor(diff / 3600000);
    const d = Math.floor(diff / 86400000);
    if (min < 1) return 'Agora';
    if (min < 60) return `Há ${min} min`;
    if (h < 24) return `Há ${h} h`;
    if (d < 7) return `Há ${d} d`;
    return new Date(visitedAt).toLocaleDateString('pt-BR');
}

function chapterLabel(ch: NonNullable<HistoryEntry['recentChapters']>[number]): string {
    const n = ch.numero;
    if (n !== '' && n != null) return String(n);
    return String(ch.id);
}

export default function HistoricoPage() {
    const [list, setList] = useState<HistoryEntry[]>(() => getHistory());

    const refresh = useCallback(() => setList(getHistory()), []);

    useEffect(() => {
        const onStorage = (e: StorageEvent) => {
            if (e.key === HISTORY_STORAGE_KEY) refresh();
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, [refresh]);

    const handleClear = useCallback(() => {
        if (!list.length) return;
        if (!window.confirm('Limpar todo o histórico neste dispositivo?')) return;
        clearHistory();
        setList([]);
    }, [list.length]);

    const shadowClass =
        'rounded-full bg-gradient-to-br from-[#ff6b35] to-[#e55a2b] opacity-[0.25] animate-float blur-3xl pointer-events-none z-0';

    const DecorativeShadows = () => (
        <>
            <div className={`fixed top-[-100px] left-[-100px] w-[200px] h-[200px] ${shadowClass}`} />
        </>
    );

    return (
        <main className="min-h-screen relative overflow-x-hidden bg-neutral-950">
            <DecorativeShadows />

            <div className="max-w-7xl mx-auto px-4 md:px-8 pt-24 lg:pt-28 pb-24 lg:pb-28 relative z-10">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-10">
                    <div className="min-w-0">
                        <SectionTitle icon={History}>Histórico local</SectionTitle>
                        <p className="text-white/50 text-sm max-w-xl -mt-4 md:-mt-5 leading-relaxed">
                            Últimas obras e capítulos que você abriu neste dispositivo. Tudo fica guardado localmente no
                            navegador.
                        </p>
                    </div>
                    {list.length > 0 && (
                        <button
                            type="button"
                            onClick={handleClear}
                            className="shrink-0 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-white/10 bg-white/[0.03] text-white/70 hover:text-white hover:bg-white/[0.07] hover:border-orange-500/30 transition-colors text-sm font-medium"
                        >
                            <Trash2 size={18} className="text-orange-400/90" />
                            Limpar histórico
                        </button>
                    )}
                </div>

                {list.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 md:py-28 text-center rounded-2xl border border-dashed border-white/10 bg-white/[0.02]">
                        <div className="w-20 h-20 rounded-full bg-white/[0.06] flex items-center justify-center mb-5">
                            <History className="w-10 h-10 text-white/25" strokeWidth={1.5} />
                        </div>
                        <p className="text-white/70 font-medium mb-1">Nada por aqui ainda</p>
                        <p className="text-white/45 text-sm max-w-sm px-4">
                            Abra uma obra ou um capítulo: elas passam a aparecer aqui para acesso rápido.
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                        {list.map((entry) => (
                            <article
                                key={`${entry.id}-${entry.visitedAt}`}
                                className="group flex flex-col rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden hover:border-orange-500/35 hover:bg-white/[0.04] transition-all duration-300"
                            >
                                <Link to={generateWorkLink(entry.id)} className="block min-w-0">
                                    <div className="aspect-[2/3] relative bg-neutral-900/80 overflow-hidden">
                                        <ObraCoverImage
                                            src={getObraImageUrl(entry.id, entry.imagem, 480) || ''}
                                            alt={entry.nome || 'Obra'}
                                            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                                            iconClassName="h-[22%] w-[22%] min-h-8 min-w-8 text-white/25"
                                        />
                                    </div>
                                    <div className="p-3 md:p-3.5">
                                        <h3 className="font-semibold text-white text-sm leading-snug line-clamp-2 group-hover:text-[#ff6b35] transition-colors">
                                            {entry.nome || 'Obra'}
                                        </h3>
                                        <p className="text-white/40 text-[11px] md:text-xs mt-1.5">{formatWhen(entry.visitedAt)}</p>
                                    </div>
                                </Link>
                                {!!entry.recentChapters?.length && (
                                    <div className="px-3 pb-3 md:px-3.5 md:pb-3.5 -mt-1 flex flex-wrap gap-1.5">
                                        {entry.recentChapters.slice(0, 3).map((ch) => (
                                            <Link
                                                key={`${entry.id}-${ch.id}-${ch.visitedAt}`}
                                                to={generateChapterLink(ch.id)}
                                                title={ch.nome || `Capítulo ${chapterLabel(ch)}`}
                                                className="px-2.5 py-1 rounded-lg bg-white/[0.06] border border-white/[0.08] text-[11px] font-medium text-white/80 hover:text-orange-300 hover:border-orange-500/35 hover:bg-orange-500/10 transition-colors"
                                            >
                                                Cap. {chapterLabel(ch)}
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </article>
                        ))}
                    </div>
                )}
            </div>
        </main>
    );
}
